import { z } from 'zod';

import { EmptyResponseSchema } from '../../../api/schemas';
import { adminApiDelete, adminApiGet, adminApiPost } from '../client';
import {
  PaymentsFeedResponseSchema,
  type PaymentsFeed,
  AdminPaymentListResponseSchema,
  AdminPaymentResponseSchema,
  AdminPaymentSchema,
  type AdminPayment,
  type RecordablePaymentMethod,
} from '../schemas';

/** Without a businessId this is the whole ledger, for the cross-business Pagos page. */
export function listPayments(businessId?: string): Promise<{ data: AdminPayment[] }> {
  const query = businessId ? `?businessId=${encodeURIComponent(businessId)}` : '';
  return adminApiGet(`/v1/admin/payments${query}`, AdminPaymentListResponseSchema);
}

export type CreatePaymentInput = {
  businessId: string;
  amountCents: number;
  method: RecordablePaymentMethod;
  paidOn: string;
  periodMonth: string;
  note?: string;
};

export function createPayment(
  input: CreatePaymentInput,
  idempotencyKey: string,
): Promise<{ data: AdminPayment }> {
  return adminApiPost('/v1/admin/payments', AdminPaymentResponseSchema, input, idempotencyKey);
}

export type PaymentDecision = { decision: 'confirmar' | 'rechazar'; note?: string };

/** Confirms or rejects a Yappy or transfer an owner reported from /pago. */
export function decidePayment(
  paymentId: string,
  decision: PaymentDecision,
  idempotencyKey: string,
): Promise<{ data: AdminPayment }> {
  return adminApiPost(
    `/v1/admin/payments/${encodeURIComponent(paymentId)}/decision`,
    AdminPaymentResponseSchema,
    decision,
    idempotencyKey,
  );
}

export const BankAnswerSchema = z.enum(['paid', 'declined', 'pending']);
export type BankAnswer = z.infer<typeof BankAnswerSchema>;

const BankCheckResponseSchema = z.object({ data: z.object({ answer: BankAnswerSchema, payment: AdminPaymentSchema }) });
export type BankCheck = z.infer<typeof BankCheckResponseSchema>['data'];

/**
 * "Verificar con el banco": asks PagueloFácil or PayPal about one open card or
 * PayPal attempt. Only the bank's answer settles it; a paid answer confirms it
 * with this admin on record. Safe to repeat.
 */
export function verifyWithBank(paymentId: string, idempotencyKey: string): Promise<{ data: BankCheck }> {
  return adminApiPost(
    `/v1/admin/payments/${encodeURIComponent(paymentId)}/verify`,
    BankCheckResponseSchema,
    undefined,
    idempotencyKey,
  );
}

/**
 * With `expectedVersion` (the payment's `version` as the list read it) the
 * server locks the payment and refuses to delete one that changed meanwhile:
 * 409 `version_conflict` with the payment as the list shows it now.
 */
export function deletePayment(paymentId: string, idempotencyKey: string, expectedVersion?: number): Promise<undefined> {
  return adminApiDelete(
    `/v1/admin/payments/${encodeURIComponent(paymentId)}`,
    EmptyResponseSchema,
    idempotencyKey,
    expectedVersion === undefined ? undefined : { expectedVersion },
  );
}

/** "Pagos que entraron": the latest confirmed and reported payments, with today's and this month's totals. */
export async function getPaymentsFeed(): Promise<PaymentsFeed> {
  const response = await adminApiGet('/v1/admin/payments/feed', PaymentsFeedResponseSchema);
  return response.data;
}
